import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Observable, of, throwError } from 'rxjs';
import { map, switchMap, catchError } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import * as CryptoJS from 'crypto-js';

export interface SignatureHeaders {
  'x-timestamp': string;
  'x-signature': string;
}

// Serviço responsável por sincronizar o tempo com o backend (/tempo) e calcular
// a assinatura HMAC-SHA256 para as requisições, conforme validação do back-end.
@Injectable({ 
  providedIn: 'root'
})
export class SignatureService {
  private serverTimestamp0: number | null = null;
  private localTimestamp0: number | null = null;
  private syncInProgress: Observable<void> | null = null;
  private readonly apiBase = environment.URL_API;
  private readonly chave = environment.CHAVE_ASSINATURA || '';

  constructor(private http: HttpClient) {}

  // Verifica se já temos tempo sincronizado com o servidor.
  isSynced(): boolean {
    return this.serverTimestamp0 !== null && this.localTimestamp0 !== null;
  }

  // Obtém o timestamp atual em milissegundos (baseado no tempo do servidor + offset local).
  // Deve ser chamado após sync().
  getCurrentTimestamp(): number {
    if (this.serverTimestamp0 === null || this.localTimestamp0 === null) {
      return Date.now();
    }
    const delta = Date.now() - this.localTimestamp0;
    return this.serverTimestamp0 + delta;
  }

  // Sincroniza o tempo com o backend via GET /tempo e armazena para contagem a partir daí.
  // Se já houver um sync em andamento, retorna o mesmo Observable.
  sync(): Observable<void> {
    if (this.syncInProgress) {
      return this.syncInProgress;
    }
    const url = `${this.apiBase}/tempo`;
    this.syncInProgress = this.http.get(url).pipe(
      map((res: any) => {
        const serverMs = typeof res === 'number'
          ? res
          : (res?.tempo_servidor ?? res?.tempo ?? res?.timestamp ?? res?.data ?? res);
        if (typeof serverMs !== 'number') {
          throw new Error('Resposta de /tempo inválida: esperado número (timestamp em ms)');
        }
        this.serverTimestamp0 = serverMs;
        this.localTimestamp0 = Date.now();
        this.syncInProgress = null;
      }),
      catchError(err => {
        this.syncInProgress = null;
        this.serverTimestamp0 = Date.now();
        this.localTimestamp0 = Date.now();
        return of(undefined);
      })
    );
    return this.syncInProgress;
  }

  ensureSyncAndGetHeaders(method: string, url: string): Observable<SignatureHeaders> {
    const urlSemQuery = url.split('?')[0];
    const doGetHeaders = (): Observable<SignatureHeaders> =>
      of(null).pipe(
        switchMap(() => this.computeHeadersAsync(method, urlSemQuery))
      );

    if (this.isSynced()) {
      return doGetHeaders();
    }
    return this.sync().pipe(switchMap(() => doGetHeaders()));
  }

  // Calcula x-timestamp e x-signature (HMAC-SHA256 em hex) de forma assíncrona.
  // stringParaAssinar = metodo + ":" + url + ":" + timestampStr (igual ao backend).
   
  private computeHeadersAsync(method: string, url: string): Observable<SignatureHeaders> {
    return new Observable(observer => {
      this.computeSignature(method, url)
        .then(headers => {
          observer.next(headers);
          observer.complete();
        })
        .catch(err => observer.error(err));
    });
  }

  private async computeSignature(method: string, url: string): Promise<SignatureHeaders> {
    const timestamp = this.getCurrentTimestamp();
    const timestampStr = String(timestamp);
    const stringParaAssinar = `${method}:${url}:${timestampStr}`;

    const signature = await this.hmacSha256Hex(stringParaAssinar, this.chave);

    return {
      'x-timestamp': timestampStr,
      'x-signature': signature
    };
  }


  private async hmacSha256Hex(message: string, secret: string): Promise<string> {
    if (typeof crypto !== 'undefined' && crypto.subtle) {
      try {
        return await this.hmacSha256Subtle(message, secret);
      } catch {
        return this.hmacSha256Fallback(message, secret);
      }
    }
    return this.hmacSha256Fallback(message, secret);
  }

  private async hmacSha256Subtle(message: string, secret: string): Promise<string> {
    const encoder = new TextEncoder();
    const keyData = encoder.encode(secret);

    const key = await crypto.subtle.importKey(
      'raw',
      keyData,
      { name: 'HMAC', hash: 'SHA-256' },
      false,
      ['sign']
    );

    const signature = await crypto.subtle.sign(
      'HMAC',
      key,
      encoder.encode(message)
    );

    const hex = Array.from(new Uint8Array(signature))
      .map(b => {
        const h = b.toString(16);
        return h.length === 1 ? '0' + h : h;
      })
      .join('');

    return hex;
  }

  // Fallback para criptografia com CryptoJS
  private hmacSha256Fallback(message: string, secret: string): string {
    const hash = CryptoJS.HmacSHA256(message, secret);
    return hash.toString(CryptoJS.enc.Hex);
  }
}